import type { KeyboardEvent } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Plus, Check, Search } from "lucide-react";
import clsx from "clsx";
import { filterPanelSelectOptions } from "../filterPanelSelectOptions";
import { PanelSelectDot } from "../PanelSelectDot";
import type { PanelSelectListProps } from "./types";

const MIN_WIDTH = 208;
const MAX_WIDTH = 320;

export const PanelSelectList = ({
  options,
  value,
  ariaLabel,
  heading,
  width,
  onActiveChange,
  onPick,
  onCreate,
  createLabel,
}: PanelSelectListProps) => {
  const { t } = useTranslation();
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLLIElement>(null);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const matches = useMemo(
    () => filterPanelSelectOptions({ options, query }),
    [options, query],
  );
  const typed = query.trim();
  // Only for text that is not already an option: making "A33" twice would be two rows for one way.
  const canCreate =
    Boolean(onCreate) &&
    typed.length > 0 &&
    !options.some((option) => option.label.toLowerCase() === typed.toLowerCase());

  // Boards carry no plan colour, and a column of identical grey discs down a
  // list of them encodes nothing. Decided per list, not per row, so the labels
  // still line up when one run among eighty has no colour yet.
  const hasColours = useMemo(
    () => options.some((option) => Boolean(option.color)),
    [options],
  );

  // Computed outside the updater, never in it: the highlight has to be
  // announced upward, and an updater React may run twice would announce twice.
  const step = useCallback(
    (delta: number) => {
      if (matches.length === 0) return;
      const next = (activeIndex + delta + matches.length) % matches.length;
      setActiveIndex(next);
      onActiveChange?.(matches[next]?.value ?? null);
    },
    [activeIndex, matches, onActiveChange],
  );

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(index);
      onActiveChange?.(matches[index]?.value ?? null);
    },
    [matches, onActiveChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        step(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        step(-1);
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        goTo(0);
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        goTo(Math.max(matches.length - 1, 0));
        return;
      }
      if (event.key !== "Enter") return;
      event.preventDefault();
      const active = matches[activeIndex];
      if (!active) return;
      onPick(active.value);
    },
    [activeIndex, goTo, matches, onPick, step],
  );

  // Narrowing eighty runs to four must leave the highlight on one of the four.
  const narrow = useCallback(
    (next: string) => {
      setQuery(next);
      setActiveIndex(0);
      const narrowed = filterPanelSelectOptions({ options, query: next });
      onActiveChange?.(narrowed[0]?.value ?? null);
    },
    [onActiveChange, options],
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <div
      // Sized to the control it belongs to and capped: the OS menu it replaced
      // opened twenty-five rows tall across the drawing.
      style={{ width: Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH) }}
      onClick={(event) => event.stopPropagation()}
      className="overflow-hidden rounded-lg border border-slate-300 bg-white shadow-lg"
    >
      {/* What the list is FOR. On the icon trigger this is the only thing that
          answers "what does this glyph do", and the rule it satisfies is
          KARL.md's: gloss the glyph rather than delete it. */}
      <div className="border-b border-slate-200 px-2.5 pt-2 text-[13px] font-semibold uppercase tracking-wide text-slate-500">
        {heading}
      </div>
      <div className="flex items-center gap-2 border-b border-slate-200 px-2.5">
        <Search size={15} aria-hidden className="flex-none text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            matches[activeIndex] ? optionId(activeIndex) : undefined
          }
          aria-label={t("smartSurveyor.cablesBeta.panelSelectFilter")}
          placeholder={t("smartSurveyor.cablesBeta.panelSelectFilter")}
          value={query}
          onChange={(event) => narrow(event.target.value)}
          onKeyDown={handleKeyDown}
          className="h-10 w-full min-w-0 bg-transparent text-[14px] text-slate-800 placeholder:text-slate-400 focus:outline-none"
        />
      </div>

      {matches.length === 0 && !canCreate ? (
        <div className="px-2.5 py-3 text-[13px] text-slate-500">
          {t("smartSurveyor.cablesBeta.panelSelectNoMatches")}
        </div>
      ) : matches.length === 0 ? null : (
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="max-h-[17rem] overflow-y-auto py-1"
        >
          {matches.map((option, index) => {
            const isActive = index === activeIndex;
            const isSelected = option.value === value;
            return (
              <li
                key={option.value}
                id={optionId(index)}
                ref={isActive ? activeRef : undefined}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => goTo(index)}
                onClick={() => onPick(option.value)}
                className={clsx(
                  "flex cursor-pointer items-center gap-2 px-2.5 py-2 text-[14px]",
                  isActive ? "bg-blue-50 text-blue-800" : "text-slate-800",
                )}
              >
                {hasColours && <PanelSelectDot color={option.color} />}
                <span className="min-w-0 flex-1 truncate" title={option.label}>
                  {option.label}
                </span>
                {isSelected && (
                  <Check size={16} className="flex-none text-blue-600" />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canCreate && (
        <button
          type="button"
          data-testid="panel-select-create"
          onClick={() => onCreate?.(typed)}
          className="flex w-full items-center gap-2 border-t border-slate-100 px-2.5 py-2.5 text-left text-[14px] font-medium text-blue-700 hover:bg-blue-50"
        >
          <Plus size={15} className="flex-none" />
          <span className="min-w-0 flex-1 truncate">
            {createLabel ? createLabel(typed) : typed}
          </span>
        </button>
      )}
    </div>
  );
};
